// src/pages/Admin/DetailsAdherents/DetailsAdherent.jsx

import React, { useMemo, useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../hooks/useScollToTop";
import ScrollIndicator from "../../Home/ScrollIndicator";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import { useUser } from "../../Home/UserContext";

import {
  FaAddressCard,
  FaCalendarCheck,
  FaClipboardList,
  FaFileAlt,
  FaIdCard,
  FaLock,
  FaMedal,
  FaMoneyCheckAlt,
  FaSyncAlt,
  FaTrophy,
  FaUserCog,
  FaUsers,
  FaChevronDown,
  FaChevronUp,
  FaExpandAlt,
  FaCompressAlt,
} from "react-icons/fa";

/**
 * On réutilise le hook et les composants fonctionnels de la version Dashboard.
 * Ajuste ces chemins seulement si ton dossier Dashboard/Admin/DetailsAdherents
 * n'est pas exactement à cet emplacement.
 */
import { useAdherentDashboard } from "./../../../dashboard/pages/admin/DetailsAdherent/hooks/useAdherentDashboard";

import AdherentFiltersCard from "./../../../dashboard/pages/admin/DetailsAdherent/components/AdherentFiltersCard";
import AdherentKpiCards from "./../../../dashboard/pages/admin/DetailsAdherent/components/AdherentKpiCards";
import AdherentHeroCard from "./../../../dashboard/pages/admin/DetailsAdherent/components/AdherentHeroCard";
import EmptyAdherentState from "./../../../dashboard/pages/admin/DetailsAdherent/components/EmptyAdherentState";

import IdentityPanel from "./../../../dashboard/pages/admin/DetailsAdherent/components/IdentityPanel";
import GroupsCategoriesPanel from "./../../../dashboard/pages/admin/DetailsAdherent/components/GroupsCategoriesPanel";
import ManagersPanel from "./../../../dashboard/pages/admin/DetailsAdherent/components/ManagersPanel";
import DossierPanel from "./../../../dashboard/pages/admin/DetailsAdherent/components/DossierPanel";
import ReglementPanel from "./../../../dashboard/pages/admin/DetailsAdherent/components/ReglementPanel";
import BeltsPanel from "./../../../dashboard/pages/admin/DetailsAdherent/components/BeltsPanel";
import CompetitionsPanel from "./../../../dashboard/pages/admin/DetailsAdherent/components/CompetitionsPanel";
import PalmaresPanel from "./../../../dashboard/pages/admin/DetailsAdherent/components/PalmaresPanel";
import AchatsPanel from "./../../../dashboard/pages/admin/DetailsAdherent/components/AchatsPanel";
import AttendancePanel from "./../../../dashboard/pages/admin/DetailsAdherent/components/AttendancePanel";

const DETAILS_SECTIONS = [
  {
    key: "identity",
    id: "identity",
    eyebrow: "Profil",
    title: "Identité & coordonnées",
    shortTitle: "Identité",
    description: "Informations administratives principales de l’adhérent.",
    icon: FaAddressCard,
    defaultOpen: true,
  },
  {
    key: "groups",
    id: "groups",
    eyebrow: "Club",
    title: "Groupes & catégories",
    shortTitle: "Groupes",
    description:
      "Groupes d’entraînement, catégorie d’âge et catégorie de poids.",
    icon: FaUsers,
    defaultOpen: true,
  },
  {
    key: "managers",
    id: "managers",
    eyebrow: "Famille / compte",
    title: "Gestionnaires liés",
    shortTitle: "Gestionnaires",
    description: "Utilisateurs responsables ou associés à cet adhérent.",
    icon: FaUserCog,
    defaultOpen: true,
  },
  {
    key: "dossier",
    id: "dossier",
    eyebrow: "Inscription",
    title: "Dossier d’inscription",
    shortTitle: "Dossier",
    description:
      "Certificat médical, licence, formulaire signé, Drive et validations.",
    icon: FaFileAlt,
    defaultOpen: true,
  },
  {
    key: "reglement",
    id: "reglement",
    eyebrow: "Paiement",
    title: "Règlement d’inscription",
    shortTitle: "Règlement",
    description:
      "Licence, cotisation, réductions, Pass’Sport et statut du règlement.",
    icon: FaClipboardList,
    defaultOpen: true,
  },
  {
    key: "belts",
    id: "belts",
    eyebrow: "Progression",
    title: "Passages de ceintures",
    shortTitle: "Ceintures",
    description: "Historique des grades et passages de ceintures.",
    icon: FaMedal,
    defaultOpen: false,
  },
  {
    key: "competitions",
    id: "competitions",
    eyebrow: "Sportif",
    title: "Compétitions",
    shortTitle: "Compétitions",
    description: "Inscriptions, validations, résultats et classements.",
    icon: FaTrophy,
    defaultOpen: false,
  },
  {
    key: "palmares",
    id: "palmares",
    eyebrow: "Résultats",
    title: "Palmarès",
    shortTitle: "Palmarès",
    description: "Résultats sportifs et observations.",
    icon: FaMedal,
    defaultOpen: false,
  },
  {
    key: "achats",
    id: "achats",
    eyebrow: "Boutique",
    title: "Achats & commandes",
    shortTitle: "Achats",
    description: "Achats effectués via les comptes gestionnaires.",
    icon: FaMoneyCheckAlt,
    defaultOpen: false,
  },
  {
    key: "attendance",
    id: "attendance",
    eyebrow: "Présences",
    title: "Présences & absences",
    shortTitle: "Présences",
    description: "Historique de présence de l’adhérent.",
    icon: FaCalendarCheck,
    defaultOpen: false,
  },
];

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getUserRoleId(user) {
  return Number(
    user?.role_id ??
      user?.roleId ??
      user?.role?.id ??
      user?.Role?.id ??
      user?.user_role_id ??
      0
  );
}

function buildDefaultOpenSections() {
  return DETAILS_SECTIONS.reduce((acc, section) => {
    acc[section.key] = section.defaultOpen;
    return acc;
  }, {});
}

function getAdherentName(adherent) {
  if (!adherent) return "Aucun adhérent sélectionné";

  return `${String(adherent?.nom || "").toUpperCase()} ${
    adherent?.prenom || ""
  }`.trim();
}

function scrollToSection(sectionId) {
  if (!sectionId) return;

  const target = document.getElementById(sectionId);
  if (!target) return;

  const offset = 130;
  const top = target.getBoundingClientRect().top + window.scrollY - offset;

  window.scrollTo({
    top,
    behavior: "smooth",
  });
}

function AccessDeniedPanel() {
  return (
    <section className="overflow-hidden rounded-[2rem] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

      <div className="p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-300">
            <FaLock />
          </span>

          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Détail adhérent
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs du club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DetailsMobileHero({
  selectedAdherent,
  seasonFilter,
  selectedAdherentId,
  totalFiltered,
  loadingPage,
  loadingDetails,
  onRefresh,
}) {
  return (
    <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="relative overflow-hidden bg-gradient-to-br from-[#013E8E] via-indigo-700 to-[#4CA82F] px-5 py-6 text-white sm:px-7">
        <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-20 -left-16 h-48 w-48 rounded-full bg-emerald-300/20 blur-3xl" />

        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] ring-1 ring-white/20">
              <FaIdCard />
              Fiche complète adhérent
            </div>

            <h1 className="mt-4 text-2xl font-bold leading-tight sm:text-3xl">
              {getAdherentName(selectedAdherent)}
            </h1>

            <p className="mt-3 max-w-3xl text-sm leading-6 text-white/90">
              Identité, groupes, dossier d’inscription, règlement, ceintures,
              compétitions, palmarès, achats et présences sur une page optimisée
              mobile.
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <span className="rounded-2xl bg-white/15 px-3 py-2 text-xs font-bold ring-1 ring-white/20">
                Saison {seasonFilter || "toutes"}
              </span>

              {selectedAdherentId ? (
                <span className="rounded-2xl bg-white/15 px-3 py-2 text-xs font-bold ring-1 ring-white/20">
                  Adhérent #{selectedAdherentId}
                </span>
              ) : null}

              <span className="rounded-2xl bg-white/15 px-3 py-2 text-xs font-bold ring-1 ring-white/20">
                {totalFiltered} résultat{totalFiltered > 1 ? "s" : ""}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={onRefresh}
            disabled={loadingPage || loadingDetails || !selectedAdherentId}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-white px-4 text-sm font-bold text-slate-900 shadow-lg shadow-black/10 transition hover:-translate-y-0.5 hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSyncAlt
              className={loadingPage || loadingDetails ? "animate-spin" : ""}
            />
            Actualiser
          </button>
        </div>
      </div>
    </section>
  );
}

function SectionControls({ openedCount, onOpenAll, onCloseAll }) {
  return (
    <section className="overflow-hidden rounded-[2rem] border border-white/70 bg-white/85 shadow-xl shadow-slate-200/70 backdrop-blur dark:border-slate-800/80 dark:bg-slate-900/70 dark:shadow-black/20">
      <div className="border-b border-slate-200/70 bg-white/70 px-3 py-3 dark:border-slate-800 dark:bg-slate-900/60 sm:px-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
              Lecture mobile
            </p>

            <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {openedCount} section{openedCount > 1 ? "s" : ""} ouverte
              {openedCount > 1 ? "s" : ""}. Les blocs lourds restent pliables
              pour garder la fiche lisible sur smartphone.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:flex">
            <button
              type="button"
              onClick={onOpenAll}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 text-sm font-bold text-white shadow-lg shadow-blue-900/20 transition hover:-translate-y-0.5 hover:bg-indigo-800"
            >
              <FaExpandAlt />
              Tout ouvrir
            </button>

            <button
              type="button"
              onClick={onCloseAll}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaCompressAlt />
              Tout fermer
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function DetailsQuickNav({ sections, openSections }) {
  return (
    <nav className="sticky top-0 z-30 -mx-3 border-y border-slate-200 bg-slate-50/95 px-3 py-3 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:-mx-5 sm:px-5 lg:static lg:mx-0 lg:rounded-[2rem] lg:border lg:bg-white lg:dark:bg-slate-900">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
        Accès rapide
      </p>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {sections.map((section) => {
          const Icon = section.icon;
          const isOpen = !!openSections[section.key];

          return (
            <button
              key={section.key}
              type="button"
              onClick={() => scrollToSection(section.id)}
              className={`inline-flex shrink-0 items-center gap-2 rounded-2xl px-3 py-2 text-xs font-bold ring-1 transition ${
                isOpen
                  ? "bg-[#013E8E] text-white ring-[#013E8E]"
                  : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
              }`}
            >
              <Icon />
              {section.shortTitle}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

function MobileSection({
  id,
  eyebrow,
  title,
  description,
  icon: Icon,
  isOpen,
  onToggle,
  children,
}) {
  return (
    <section
      id={id}
      className="scroll-mt-28 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900"
    >
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start justify-between gap-3 px-4 py-4 text-left transition active:scale-[0.99] dark:hover:bg-slate-800/60 sm:px-5"
      >
        <div className="flex min-w-0 gap-3">
          {Icon ? (
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              <Icon />
            </span>
          ) : null}

          <div className="min-w-0">
            {eyebrow ? (
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                {eyebrow}
              </p>
            ) : null}

            <h2 className="mt-1 text-lg font-bold leading-snug text-slate-900 dark:text-white sm:text-xl">
              {title}
            </h2>

            {description ? (
              <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                {description}
              </p>
            ) : null}
          </div>
        </div>

        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200">
          {isOpen ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      </button>

      {isOpen ? (
        <div className="border-t border-slate-200 px-3 py-4 dark:border-slate-800 sm:px-5">
          <div className="min-w-0 overflow-hidden">{children}</div>
        </div>
      ) : null}
    </section>
  );
}

function renderSectionContent({
  sectionKey,
  selectedAdherent,
  ageCategory,
  weightCategory,
  managers,
  roles,
  dossier,
  reglement,
  belts,
  competitions,
  palmares,
  achats,
  attendances,
  seasonFilter,
}) {
  switch (sectionKey) {
    case "identity":
      return <IdentityPanel adherent={selectedAdherent} />;

    case "groups":
      return (
        <GroupsCategoriesPanel
          adherent={selectedAdherent}
          ageCategory={ageCategory}
          weightCategory={weightCategory}
        />
      );

    case "managers":
      return <ManagersPanel managers={managers} roles={roles} />;

    case "dossier":
      return <DossierPanel dossier={dossier} />;

    case "reglement":
      return (
        <ReglementPanel
          reglement={reglement}
          saison={selectedAdherent?.saison_active || seasonFilter}
        />
      );

    case "belts":
      return <BeltsPanel belts={belts} adherent={selectedAdherent} />;

    case "competitions":
      return <CompetitionsPanel competitions={competitions} />;

    case "palmares":
      return <PalmaresPanel palmares={palmares} />;

    case "achats":
      return <AchatsPanel achats={achats} />;

    case "attendance":
      return <AttendancePanel attendances={attendances} />;

    default:
      return null;
  }
}

export default function DetailsAdherent() {
  useScrollToTop();

  const { user } = useUser();

  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const userRole = getUserRoleId(user || storedUser);
  const isAdmin = userRole === 1;

  const [openSections, setOpenSections] = useState(() =>
    buildDefaultOpenSections()
  );

  const {
    selectedAdherent,
    selectedAdherentId,
    filteredAdherents,

    seasons,
    seasonFilter,
    setSeasonFilter,

    dojos,
    selectedDojo,
    handleDojoChange,

    groups,
    selectedGroup,
    setSelectedGroup,

    loadingPage,
    loadingDetails,
    errorMsg,

    selectAdherent,
    refreshCurrentAdherent,

    roles,
    managers,
    dossier,
    reglement,
    belts,
    competitions,
    palmares,
    achats,
    attendances,

    ageCategory,
    weightCategory,
    stats,
  } = useAdherentDashboard();

  const openedCount = useMemo(() => {
    return Object.values(openSections).filter(Boolean).length;
  }, [openSections]);

  const toggleSection = (key) => {
    setOpenSections((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  const openAll = () => {
    setOpenSections(
      DETAILS_SECTIONS.reduce((acc, section) => {
        acc[section.key] = true;
        return acc;
      }, {})
    );
  };

  const closeAll = () => {
    setOpenSections(
      DETAILS_SECTIONS.reduce((acc, section) => {
        acc[section.key] = false;
        return acc;
      }, {})
    );
  };

  return (
    <section className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <ScrollIndicator />

      <div className="pt-[96px] sm:pt-[120px] lg:pt-[160px]" aria-hidden />

      <main className="mx-auto w-full max-w-7xl px-3 pb-24 sm:px-6 lg:px-8">
        <HeaderTitle
          role="Administrateur"
          title="Détails adhérent"
          subtitle="Consulte la fiche complète d’un adhérent : identité, groupes, dossier, règlement, ceintures, compétitions, palmarès, achats et présences."
          icon={FaIdCard}
          align="left"
          className="mb-4"
        />

        <div className="mb-5 sm:mb-7">
          <DashboardNavigation role={userRole} />
        </div>

        {!isAdmin ? (
          <AccessDeniedPanel />
        ) : (
          <div className="space-y-4 sm:space-y-6">
            <DetailsMobileHero
              selectedAdherent={selectedAdherent}
              seasonFilter={seasonFilter}
              selectedAdherentId={selectedAdherentId}
              totalFiltered={filteredAdherents.length}
              loadingPage={loadingPage}
              loadingDetails={loadingDetails}
              onRefresh={refreshCurrentAdherent}
            />

            <AdherentFiltersCard
              seasons={seasons}
              seasonFilter={seasonFilter}
              setSeasonFilter={setSeasonFilter}
              dojos={dojos}
              selectedDojo={selectedDojo}
              handleDojoChange={handleDojoChange}
              groups={groups}
              selectedGroup={selectedGroup}
              setSelectedGroup={setSelectedGroup}
              adherents={filteredAdherents}
              selectedAdherentId={selectedAdherentId}
              selectAdherent={selectAdherent}
              loading={loadingPage}
              loadingDetails={loadingDetails}
              refreshCurrentAdherent={refreshCurrentAdherent}
              totalFiltered={filteredAdherents.length}
              errorMsg={errorMsg}
            />

            {!selectedAdherent ? (
              <EmptyAdherentState loading={loadingPage || loadingDetails} />
            ) : (
              <>
                <AdherentHeroCard
                  adherent={selectedAdherent}
                  ageCategory={ageCategory}
                  weightCategory={weightCategory}
                />

                <AdherentKpiCards stats={stats} loading={loadingDetails} />

                <SectionControls
                  openedCount={openedCount}
                  onOpenAll={openAll}
                  onCloseAll={closeAll}
                />

                <section className="overflow-hidden rounded-[2rem] border border-white/70 bg-white/85 shadow-xl shadow-slate-200/70 backdrop-blur dark:border-slate-800/80 dark:bg-slate-900/70 dark:shadow-black/20">
                  <div className="border-b border-slate-200/70 bg-white/70 px-3 py-3 dark:border-slate-800 dark:bg-slate-900/60 sm:px-5">
                    <DetailsQuickNav
                      sections={DETAILS_SECTIONS}
                      openSections={openSections}
                    />
                  </div>

                  <div className="space-y-4 p-3 sm:p-5 lg:p-6">
                    {DETAILS_SECTIONS.map((section) => (
                      <MobileSection
                        key={section.key}
                        id={section.id}
                        eyebrow={section.eyebrow}
                        title={section.title}
                        description={section.description}
                        icon={section.icon}
                        isOpen={!!openSections[section.key]}
                        onToggle={() => toggleSection(section.key)}
                      >
                        {renderSectionContent({
                          sectionKey: section.key,
                          selectedAdherent,
                          ageCategory,
                          weightCategory,
                          managers,
                          roles,
                          dossier,
                          reglement,
                          belts,
                          competitions,
                          palmares,
                          achats,
                          attendances,
                          seasonFilter,
                        })}
                      </MobileSection>
                    ))}
                  </div>
                </section>
              </>
            )}
          </div>
        )}
      </main>

      <ToastContainer />
    </section>
  );
}